import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import CurrencyFlag from 'react-currency-flags'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { createPrice, updatePrice } from '../../actions/certificationActions'
import { getAllOrganizations } from '../../actions/organizationsActions'
import { getCurrencies } from '../../actions/currenciesActions'
import { DOCUMENT_BODY_REFERENCE, MODAL_MENU_PORTAL_STYLES } from '../../misc/Constants'
import { showGenericError } from '../../utility/certificationBuilder'
import { useForm } from './useForm'
import { labelledSelectComponents } from './selectComponents'
import Modal from '../common/Modal'
import Input from '../common/form/Input'
import Select from '../common/form/Select'
import CancelButton from '../common/buttons/CancelButton'
import SubmitButton from '../common/buttons/SubmitButton'

import classes from './PriceFormModal.module.scss'

const DEFAULT_CURRENCY_CODE = 'usd'

/* eslint-disable react/prop-types */
const CurrencyLabel = ({ data }) => (
  <span className={classNames('d-inline-flex align-items-center', classes.currency)}>
    <CurrencyFlag currency={data.value} />
    {data.label}
  </span>
)
/* eslint-enable react/prop-types */

const CURRENCY_COMPONENTS = labelledSelectComponents(CurrencyLabel)

const PriceFormModal = ({ isOpen, certification, price, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const organizations = useSelector(state => state.organizations.organizations)
  const currencies = useSelector(state => state.currencies)
  const { form, errors, setErrors, setField } = useForm({
    organizationId: price?.organization?.id || null,
    currencyCode: price?.currency.code || DEFAULT_CURRENCY_CODE,
    amount: price ? String(Number(price.amount)) : '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (organizations.length === 0) { dispatch(getAllOrganizations()) }
    if (currencies.length === 0) { dispatch(getCurrencies()) }
  }, [dispatch, organizations.length, currencies.length])

  const takenOrganizationIds = certification.prices.filter(current => current.id !== price?.id).map(current => current.organization?.id || null)
  const organizationOptions = [
    { value: null, label: t('certificationBuilder.prices.default') },
    ...organizations.map(organization => ({ value: organization.id, label: organization.name })),
  ].filter(option => !takenOrganizationIds.includes(option.value))
  const currencyOptions = currencies.map(currency => ({ value: currency.code, label: `${currency.name} (${currency.symbol})` }))

  const handleSubmit = (e) => {
    e.preventDefault()
    const amount = Number(form.amount)
    if (form.amount.trim() === '' || Number.isNaN(amount) || amount < 0) {
      setErrors({ amount: t('certificationBuilder.prices.form.errors.amount') })
      return
    }

    const attributes = { organizationId: form.organizationId, currencyCode: form.currencyCode, amount }
    const request = price
      ? updatePrice({ certificationId: certification.id, id: price.id, ...attributes })
      : createPrice({ certificationId: certification.id, ...attributes })

    setIsSubmitting(true)
    dispatch(request).unwrap()
      .then(() => { toast.success(t('certificationBuilder.prices.form.saveSuccess'), { position: toast.POSITION.TOP_RIGHT }); onClose() })
      .catch((apiErrors) => apiErrors?.amount || apiErrors?.organizationId || apiErrors?.base
        ? setErrors({ amount: apiErrors.amount?.[0], organization: apiErrors.organizationId?.[0] || apiErrors.base?.[0] })
        : showGenericError())
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={classes.modalContent}>
      <h2 className={classes.title}>{t(price ? 'certificationBuilder.prices.form.editTitle' : 'certificationBuilder.prices.form.createTitle')}</h2>

      <form onSubmit={handleSubmit} className={classNames('d-flex flex-column', classes.form)}>
        <div>
          <label htmlFor='priceOrganization'>{t('certificationBuilder.prices.form.organization')}</label>
          <Select
            inputId='priceOrganization'
            options={organizationOptions}
            value={organizationOptions.find(option => option.value === form.organizationId) || null}
            onChange={(option) => setField('organizationId', option.value)}
            error={errors.organization}
            isSearchable={false}
            menuPortalTarget={DOCUMENT_BODY_REFERENCE}
            styles={MODAL_MENU_PORTAL_STYLES}
          />
        </div>

        <div className={classes.pairRow}>
          <div>
            <label htmlFor='priceCurrency'>{t('certificationBuilder.prices.form.currency')}</label>
            <Select
              inputId='priceCurrency'
              options={currencyOptions}
              value={currencyOptions.find(option => option.value === form.currencyCode) || null}
              components={CURRENCY_COMPONENTS}
              onChange={(option) => setField('currencyCode', option.value)}
              isSearchable={false}
              menuPortalTarget={DOCUMENT_BODY_REFERENCE}
              styles={MODAL_MENU_PORTAL_STYLES}
            />
          </div>
          <div>
            <label htmlFor='priceAmount'>{t('certificationBuilder.prices.form.amount')} <span className='required-text'>*</span></label>
            <Input
              id='priceAmount'
              type='number'
              min={0}
              step='0.01'
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              error={errors.amount}
            />
          </div>
        </div>

        <div className='row pt-3'>
          <div className='col-6'>
            <CancelButton onClick={onClose} buttonMessage={t('common.cancel')} />
          </div>
          <div className='col-6'>
            <SubmitButton type='submit' buttonMessage={t('common.save')} loading={isSubmitting} noTopMargin />
          </div>
        </div>
      </form>
    </Modal>
  )
}

PriceFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  certification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    prices: PropTypes.array.isRequired,
  }).isRequired,
  price: PropTypes.shape({
    id: PropTypes.number.isRequired,
    amount: PropTypes.string.isRequired,
    currency: PropTypes.shape({ code: PropTypes.string.isRequired }).isRequired,
    organization: PropTypes.shape({ id: PropTypes.number.isRequired }),
  }),
  onClose: PropTypes.func.isRequired,
}

export default PriceFormModal
